import React, { useEffect, useState, useRef } from 'react';

// One look per type. Previously only `error` was styled; success / warning /
// info all rendered as the same white bubble, so a failed apply and a
// completed one were indistinguishable at a glance.
const PALETTE = {
  error: {
    bg: 'rgba(220, 53, 69, 0.95)',
    fg: 'white',
    border: 'rgba(220, 53, 69, 1)',
    track: 'rgba(255, 255, 255, 0.3)',
    icon: '⚠',
  },
  warning: {
    bg: 'rgba(255, 248, 230, 0.97)',
    fg: '#7c4a03',
    border: '#f59e0b',
    track: 'rgba(124, 74, 3, 0.2)',
    icon: '⚠',
  },
  success: {
    bg: 'rgba(240, 253, 244, 0.97)',
    fg: '#14532d',
    border: '#22c55e',
    track: 'rgba(20, 83, 45, 0.2)',
    icon: '✓',
  },
  info: {
    bg: 'rgba(255, 255, 255, 0.95)',
    fg: '#333',
    border: 'rgba(0, 0, 0, 0.2)',
    track: 'rgba(0, 0, 0, 0.15)',
    icon: null,
  },
};

function NotificationBubble({
  message,
  type = 'info',
  duration = 10000, // Default 10 seconds (null means no auto-close)
  onClose,
  showSpinner = false,
  createdAt: createdAtProp, // When the notification was raised; see below
  // Optional inline action. A long job that closes its dialog to get out of
  // the way (renderings) has nowhere to hand back its result, so the toast
  // carries the way back to it — "2 renderings ready [View]".
  actionLabel,
  onAction,
  // Where the bubble sits, for a host whose chrome is in the default spot.
  // The elevations viewer's slide-in panel is `position: fixed; left: 0` and
  // 420px wide, and `.structure-viewer-wrapper` is a stacking context that
  // caps this bubble's z-index against it — so a toast raised while a panel
  // is open renders BEHIND the panel and only its tail is visible.
  left = '88px',
}) {
  const [opacity, setOpacity] = useState(0);
  const [show, setShow] = useState(true);
  const progressCircleRef = useRef(null);
  const intervalRef = useRef(null);
  const palette = PALETTE[type] || PALETTE.info;

  // `createdAt` is what the countdown is measured from, and it is also a dep of
  // the effect that runs the countdown. It used to be a DEFAULT PARAMETER
  // (`createdAt = Date.now()`), which re-evaluates on every render — so for any
  // host that spreads a notification without one (most of them), the dep changed
  // every render, the interval was torn down and restarted, progress reset to
  // full, and the toast counted down forever without ever closing.
  //
  // Stamp it once per NOTIFICATION instead of once per render. The identity is
  // what the host actually varies; a host that does supply `createdAt` still
  // wins, and a repeat of the same message with a fresh timestamp restarts the
  // clock as it should.
  const identity = `${createdAtProp ?? ''}\u0000${message}\u0000${type}\u0000${duration}`;
  const stamp = useRef({ identity: null, at: 0 });
  if (stamp.current.identity !== identity) {
    stamp.current = { identity, at: createdAtProp ?? Date.now() };
  }
  const createdAt = stamp.current.at;

  // Calculate initial progress based on elapsed time
  const getInitialProgress = () => {
    if (duration === null || duration === undefined) return 100;
    const elapsed = Date.now() - createdAt;
    return Math.max(0, 100 - (elapsed / duration) * 100);
  };
  const [progress, setProgress] = useState(getInitialProgress());

  useEffect(() => {
    // A new notification reusing this element (same host, new message)
    // starts its own lifecycle — otherwise it inherited the previous
    // toast's timer and could vanish immediately or never appear.
    setShow(true);
    setProgress(getInitialProgress());
    // Fade in
    setTimeout(() => setOpacity(1), 10);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Auto-hide after duration (if duration is not null)
    if (duration !== null && duration !== undefined && onClose) {
      const radius = 10;
      const circumference = 2 * Math.PI * radius;

      // Check if notification has already expired on mount
      const initialElapsed = Date.now() - createdAt;
      if (initialElapsed >= duration) {
        setOpacity(0);
        setTimeout(() => {
          setShow(false);
          onClose();
        }, 300);
        return;
      }

      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = now - createdAt; // Use createdAt prop, not ref
        const remaining = Math.max(0, duration - elapsed);
        const progressPercent = (remaining / duration) * 100;

        // Update progress state
        setProgress(progressPercent);

        // Directly update the SVG circle if ref exists
        if (progressCircleRef.current) {
          const offset = circumference * (1 - progressPercent / 100);
          progressCircleRef.current.style.strokeDashoffset = offset;
        }

        if (remaining === 0) {
          clearInterval(intervalRef.current);
          setOpacity(0);
          setTimeout(() => {
            setShow(false);
            onClose();
          }, 300);
        }
      }, 100); // Update every 100ms

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdAt, message, duration]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '32px', // Exact same position as logo
        left, // default: right after the logo (32px padding + 48px logo + 8px gap)
        backgroundColor: palette.bg,
        color: palette.fg,
        padding: '0.75rem 1.25rem', // Adjusted padding to match logo height
        minHeight: '48px', // Match logo height exactly
        borderRadius: '8px', // Match scan name border radius
        border: `1px solid ${palette.border}`,
        fontSize: '14px', // Reduced text size
        fontWeight: '500', // Medium weight for readability
        lineHeight: '1.4', // Good line height for readability
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)', // Slightly stronger shadow
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        opacity: opacity,
        transition: 'opacity 0.3s ease-in-out',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        animation: 'slideInFromLeft 0.3s ease-out',
        boxSizing: 'border-box', // Include border in height calculation
      }}
    >
      {/* Animated spinner for loading states */}
      {showSpinner && (
        <div
          style={{
            width: '16px',
            height: '16px',
            border: `2px solid ${palette.track}`,
            borderTopColor: palette.fg,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      )}

      {palette.icon && (
        <span aria-hidden="true" style={{ fontSize: '15px', lineHeight: 1 }}>
          {palette.icon}
        </span>
      )}
      <span role={type === 'error' ? 'alert' : 'status'}>{message}</span>

      {actionLabel && onAction && (
        <button
          type="button"
          onClick={() => {
            setOpacity(0);
            setTimeout(() => {
              setShow(false);
              onAction();
              if (onClose) onClose();
            }, 300);
          }}
          style={{
            marginLeft: '4px',
            padding: '4px 12px',
            border: `1px solid ${palette.border}`,
            borderRadius: '6px',
            background: 'transparent',
            color: palette.fg,
            font: 'inherit',
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {actionLabel}
        </button>
      )}

      {/* Close button with circular progress (only show progress if duration is set) */}
      {onClose && (
        <div
          style={{
            position: 'relative',
            marginLeft: '8px',
            width: '24px',
            height: '24px',
            cursor: 'pointer',
          }}
          onClick={() => {
            setOpacity(0);
            setTimeout(() => {
              setShow(false);
              onClose();
            }, 300);
          }}
        >
          {/* Only show progress circle if duration is set */}
          {duration !== null &&
            duration !== undefined &&
            (() => {
              const radius = 10;
              const circumference = 2 * Math.PI * radius;
              const offset = circumference * (1 - progress / 100);

              return (
                <svg
                  width="24"
                  height="24"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: 'rotate(-90deg)',
                  }}
                >
                  {/* Background circle */}
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke={palette.track}
                    strokeWidth="2"
                    fill="none"
                  />
                  {/* Progress circle */}
                  <circle
                    ref={progressCircleRef}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke={palette.fg}
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{
                      transition: 'stroke-dashoffset 100ms linear',
                    }}
                  />
                </svg>
              );
            })()}
          {/* X button */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: palette.fg,
              fontSize: '20px',
              fontFamily: 'Arial, sans-serif',
              fontWeight: '300',
              lineHeight: '1',
            }}
          >
            ×
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default NotificationBubble;
